import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
// TODO Add flags to the country select after PLAT-1157
// import Flag from 'react-world-flags';
import {
    Form,
    useForm,
    FormInputText,
    FormInputSelect,
    Button,
    ButtonGroup,
    HeadingText60,
    HeadingText20,
    pxToRem,
} from '@modernhealth/vital-components';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { MemberAvailability, MemberProfileInput } from 'src/generated/webapp_gql';
import { Dispatch } from 'src/config/rootStore';
import { toastNotificationsService } from 'src/config/rootService';
import { config } from 'src/config/config';
import { getSortedCountries } from 'src/modules/countries/selectors/countrySelectors';
import { getSortedLanguages } from 'src/modules/languages/selectors/languageSelectors';
import { BrandWrapper } from 'src/modules/onboarding/components/BrandWrapper';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import { DefaultSupportedLocaleDisplayName, availabilityOptions } from 'src/shared/models/shared';
import {
    usaStatesOptions,
    canadaProvincesOptions,
} from 'src/modules/insuranceIntegration/models/statesOptions';
import { FormInputTimeZone } from 'src/modules/user/components/FormInputTimeZone';

enum Country {
    USA = 'United States',
    CANADA = 'Canada',
}

const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    padding: pxToRem(50),
    flex: 1,
});

const ContentWrapper = styled.div({
    marginTop: pxToRem(30),
    maxWidth: pxToRem(500),
    width: '100%',
});

interface Props {
    onSubmit?: () => void;
    onPreviousPress?: () => void;
    useBrandWrapper?: boolean;
}

const isStateRequired = v => [Country.USA, Country.CANADA].includes(v);
const isZipCodeRequired = v => [Country.USA].includes(v);

interface FormData {
    country: string;
    state: string;
    city: string;
    zipCode: string;
    languagePreferences: string;
    availability: MemberAvailability[];
    localTimezone: string;
    phoneNumber: string;
}

export const AdditionalInfo: React.FC<Props> = ({ onSubmit, useBrandWrapper, onPreviousPress }) => {
    const { t } = useTranslation();
    const user = useSelector(getUserInformation);
    const countries = useSelector(getSortedCountries);
    const languagesOptions = useSelector(getSortedLanguages);
    const dispatch = useDispatch<Dispatch>();

    const handleSubmit = async (data: FormData) => {
        try {
            await dispatch.user.updateMemberProfile({
                ...data,
                languagePreferences: data.languagePreferences ? [data.languagePreferences] : [],
            } as MemberProfileInput);
        } catch {
            toastNotificationsService.renderErrorToast(t(config.messages.genericError));
        }

        onSubmit?.();
    };

    const ErrorLabels = {
        COUNTRY: t('validation:field.combobox.required'),
        STATE: t('validation:field.combobox.required'),
        CITY: t('validation:city.required'),
        ZIP_CODE: t('validation:zip_code.required'),
        LOCAL_TIMEZONE: t('validation:local_timezone.required'),
        LANGUAGE_PREFERENCES: t('validation:field.combobox.required'),
        AVAILABILITY: t('validation:field.combobox.required'),
    };

    const formSchema = Yup.object().shape({
        country: Yup.string().required(ErrorLabels.COUNTRY),
        state: Yup.string().when('country', {
            is: isStateRequired,
            then: Yup.string().required(ErrorLabels.STATE),
        }),
        city: Yup.string().required(ErrorLabels.CITY),
        // Postal codes are complicated internationally.
        // For now, validate only US postal codes and
        // accept anything for other countries.
        zipCode: Yup.string().when('country', {
            is: isZipCodeRequired,
            then: Yup.string().trim().length(5, ErrorLabels.ZIP_CODE),
        }),
        localTimezone: Yup.string().required(ErrorLabels.LOCAL_TIMEZONE),
        languagePreferences: Yup.string().required(ErrorLabels.LANGUAGE_PREFERENCES),
        availability: Yup.array()
            .min(1, ErrorLabels.AVAILABILITY)
            .required(ErrorLabels.AVAILABILITY),
        phoneNumber: Yup.string(),
    });

    const formDefinition = useForm<FormData>({
        resolver: yupResolver(formSchema),
        defaultValues: {
            country: user.country || Country.USA,
            state: user.state,
            city: user.city,
            zipCode: user.zipCode,
            localTimezone: user.localTimezone,
            languagePreferences: user.languagePreferences[0] ?? DefaultSupportedLocaleDisplayName,
            availability: user.availability as MemberAvailability[],
            // Even though user.phoneNumber is typed as a string in
            // redux. I have found that can be null. null is a valid
            // type for this value in the MemberNode. We will enforce
            // the string type in this form and set is as a string if
            // it isn't. Null is a valid type for this value in the
            // MemberNode.
            phoneNumber: user.phoneNumber ?? '',
        },
    });

    useEffect(() => {
        const subscription = formDefinition.watch((_, { name, type }) => {
            // clear state, city and zip code since the country has changed
            if (name === 'country' && type === 'change') {
                formDefinition.setValue('state', '');
                formDefinition.setValue('zipCode', '');
                formDefinition.setValue('city', '');
                formDefinition.clearErrors(['state', 'zipCode', 'city']);
            }
        });
        return () => subscription.unsubscribe();
    }, [formDefinition]);

    const country = formDefinition.watch('country');
    const statesOptions =
        {
            [Country.USA]: usaStatesOptions,
            [Country.CANADA]: canadaProvincesOptions,
        }[country] || [];

    const form = (
        <Wrapper>
            <HeadingText60>{t('providers:additional_info.title')}</HeadingText60>
            <HeadingText20>{t('providers:additional_info.subtitle')}</HeadingText20>

            <ContentWrapper>
                <Form definition={formDefinition} onSubmit={handleSubmit}>
                    <FormInputSelect
                        name="country"
                        label={t('providers:additional_info.country_region')}
                        options={countries.map(c => ({
                            label: c.displayName,
                            // TODO Add flags to the country select
                            // after PLAT-1157
                            // label: (
                            //     <>
                            //         <Flag code={c.isoCode} width="36" height="24" />
                            //         {c.displayName}
                            //     </>
                            // ),
                            value: c.value,
                        }))}
                        required
                    />
                    {isStateRequired(country) && (
                        <FormInputSelect
                            name="state"
                            label={
                                country === Country.USA
                                    ? t('providers:additional_info.state')
                                    : t('providers:additional_info.province_or_territory')
                            }
                            options={statesOptions.map(s => ({
                                label: s.fullName,
                                value: s.stateCode,
                            }))}
                            dependentFields="country"
                            required
                        />
                    )}
                    <FormInputText
                        label={t('providers:additional_info.city')}
                        name="city"
                        required
                    />
                    <FormInputText
                        label={t('providers:additional_info.postal_code')}
                        name="zipCode"
                        required={isZipCodeRequired(country)}
                    />
                    <FormInputTimeZone
                        label={t('user:account_details.localTimezone')}
                        name="localTimezone"
                        required
                        autoDetectDefaultValue
                    />
                    <FormInputSelect
                        name="languagePreferences"
                        label={t('providers:additional_info.language_preferences')}
                        options={languagesOptions.map(l => ({
                            label: l.text,
                            value: l.value,
                        }))}
                        required
                    />
                    <FormInputSelect
                        name="availability"
                        label={t('providers:additional_info.availability')}
                        options={availabilityOptions.map(a => ({
                            label: t(
                                `providers:additional_info.availabilityTime.${a.toLowerCase()}`,
                            ),
                            value: a,
                        }))}
                        hint={t('providers:additional_info.select_all_that_match')}
                        multi
                        required
                    />
                    <FormInputText
                        name="phoneNumber"
                        label={t('providers:additional_info.phone_number')}
                    />
                    <ButtonGroup css={{ display: onPreviousPress ? 'fluid' : 'block' }}>
                        {onPreviousPress ? (
                            <Button
                                onClick={onPreviousPress}
                                variant="secondary"
                                size="large"
                                fluid
                            >
                                {t('common.previous')}
                            </Button>
                        ) : null}
                        <Button
                            type="submit"
                            size="large"
                            fluid
                            busy={formDefinition.formState.isSubmitting}
                        >
                            {t('common.continue')}
                        </Button>
                    </ButtonGroup>
                </Form>
            </ContentWrapper>
        </Wrapper>
    );

    if (useBrandWrapper) {
        return <BrandWrapper>{form}</BrandWrapper>;
    }

    return form;
};
